import React, { FCWithChildren, useRef } from 'react';
import clsx from 'clsx';
import useOnClickOutside from '@hooks/browser-events/use-on-click-outside';
import Button from '@components/atoms/button/button';

import { FocusTrap } from 'focus-trap-react';
import { useTranslation } from 'next-i18next';
import parentStyles from './drawer.module.scss';

export type PositionType = 'left' | 'right' | 'top' | 'bottom';

type DrawerProps = {
  onClose?: () => void;
  position?: PositionType;
  open: boolean;
  focusTrapActive: boolean;
  modalDialogTitleId: string;
  hideBackdrop?: boolean;
  customClass?: string;
} & React.HTMLProps<HTMLDivElement>;

const Drawer: FCWithChildren<DrawerProps> = ({
  onClose = () => {},
  position = 'right',
  open,
  focusTrapActive,
  hideBackdrop = true,
  children,
  customClass,
  modalDialogTitleId,
}) => {
  const DrawerRef = useRef(null);
  const { t } = useTranslation();

  const handleClose = () => {
    onClose();
  };

  const handleKeyDown = (event) => {
    if (event.code === 'Escape' && open) {
      event.stopPropagation();
      handleClose();
    }
  };

  useOnClickOutside(DrawerRef, () => {
    handleClose();
  });

  const { drawer, hidden } = parentStyles;

  return (
    <div
      role="dialog"
      className={clsx(drawer, !open && hidden, customClass)}
      aria-modal="true"
      aria-labelledby={modalDialogTitleId}
    >
      <FocusTrap active={open && focusTrapActive}>
        <div ref={DrawerRef}>
          <div className={clsx(parentStyles['drawer-container'], parentStyles[position])}>
            <Button
              id="close-drawer-btn"
              disableDefaultStyling
              className="vc-modal__closeBtn"
              onClick={handleClose}
              tabIndex={0}
              aria-label={t('MODAL.BUTTON.CLOSE')}
              onKeyDown={handleKeyDown}
            >
              <span className="vc-modal__closeBtn__crossLeft" />
              <span className="vc-modal__closeBtn__crossRight" />
            </Button>
            {children}
          </div>
          <div
            className={clsx(
              parentStyles['drawer-backdrop'],
              (hideBackdrop || !open) && parentStyles['drawer-backdrop-hidden'],
            )}
          />
        </div>
      </FocusTrap>
    </div>
  );
};

export default Drawer;
